import request from '@/lib/request';
import type { AxiosRequestConfig } from 'axios';

// ============ 设置模块 API 接口 ============

/**
 * 上传头像
 * POST /settings/profile/avatar
 * multipart/form-data，需要 Bearer Token
 */
export const uploadAvatar = (file: File, config?: AxiosRequestConfig) => {
  const formData = new FormData();
  formData.append('file', file);
  return request.post('/settings/uploadHeadImage', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...config,
  });
};

/**
 * 获取个人资料
 * GET /settings/profile
 * 需要 Bearer Token 认证
 */
export const getProfile = () => {
  return request.get('/settings/profile');
};

/**
 * 更新个人资料
 * PUT /settings/profile
 * 需要 Bearer Token 认证
 */
export const updateProfile = (data: {
  name?: string;
  phone?: string;
  sex?: string;
  company?: string;
  headImage?: string;
  email?: string;
  intro?: string;
}) => {
  return request.put('/settings/profile', data);
};

/**
 * 获取通知设置
 * GET /settings/notifications
 * 需要 Bearer Token 认证
 */
export const getNotificationSettings = () => {
  return request.get('/settings/notifications');
};

/**
 * 更新通知设置
 * PUT /settings/notifications
 * 需要 Bearer Token 认证
 */
export const updateNotificationSettings = (data: {
  contentCompleteNotify?: string;
  teamNotify?: string;
  productNotify?: string;
}) => {
  return request.put('/settings/notifications', data);
};

/**
 * 修改密码
 * POST /settings/security/password
 * 需要 Bearer Token 认证
 */
export const changePassword = (data: {
  oldPassword: string;
  newPassword: string;
}) => {
  return request.post('/settings/security/password', data);
};

/**
 * 创建团队
 * POST /settings/teams
 * 需要 Bearer Token 认证
 */
export const createTeam = (data: {
  teamName: string;
  description?: string;
}) => {
  return request.post('/settings/teams', data);
};

/**
 * 获取团队详情
 * GET /settings/teams/{id}
 * 需要 Bearer Token 认证
 */
export const getTeamDetail = (id: number) => {
  return request.get(`/settings/teams/${id}`);
};

/**
 * 更新团队信息
 * PUT /settings/teams/{id}
 * 需要 Bearer Token 认证
 */
export const updateTeam = (id: number, data: {
  teamName?: string;
  description?: string;
}) => {
  return request.put(`/settings/teams/${id}`, data);
};

/**
 * 获取团队成员列表
 * POST /settings/team/members/{pageNo}/{pageSize}
 * 支持状态筛选
 */
export const getTeamMembers = (data: {
  pageNo: number;
  pageSize: number;
  status?: string;
}) => {
  return request.post(`/settings/team/members/${data.pageNo}/${data.pageSize}`, {
    status: data.status || undefined,
  });
};

/**
 * 邀请成员加入团队
 * POST /settings/team
 * 需要 Bearer Token 认证
 */
export const inviteMember = (data: {
  email: string;
  role: string;
}) => {
  return request.post('/settings/team', data);
};

/**
 * 更新团队成员角色
 * PUT /settings/team/{id}
 * 需要 Bearer Token 认证
 */
export const updateMemberRole = (id: number, data: { role: string }) => {
  return request.put(`/settings/team/${id}`, data);
};

// 获取角色列表
export const getRoles = () => {
  return request.post('/yyrole/findAll');
}

/**
 * 获取消息列表
 */
export const getMessages = (params?: { category?: string; pageNo?: number; pageSize?: number }) => {
  return request.get('/settings/messages', { params });
};

/**
 * 标记消息已读
 */
export const markMessageRead = (id: number) => {
  return request.put(`/settings/messages/${id}/read`);
};

/**
 * 全部标记已读
 */
export const markAllMessagesRead = () => {
  return request.put('/settings/messages/read-all');
};

/**
 * 发送消息
 * POST /settings/messages/send
 * 需要 Bearer Token 认证
 */
export const sendMessage = (data: {
  to: string;
  content: string;
  type?: string;
}) => {
  return request.post('/settings/messages/send', data);
};

/**
 * 获取未读消息数
 */
export const getUnreadCount = () => {
  return request.get('/settings/messages/unread-count');
};

/**
 * 拒绝待审核成员
 */
export const rejectTeamMember = (memberId: number) => {
  return request.get(`/settings/teams/members/reject/${memberId}`);
};

/**
 * 审核通过成员
 * PUT /team/members/approve/{memberId}
 */
export const approveMember = (memberId: number) => {
  return request.get(`/settings/teams/members/approve/${memberId}`);
};

/**
 * 启用成员
 * PUT /settings/team/restore/{memberId}
 */
export const restoreMember = (memberId: number) => {
  return request.get(`/settings/teams/members/enable/${memberId}`);
};

/**
 * 禁用团队成员
 * PUT /settings/team/{teamId}/disable/{memberId}
 */
export const disableMember = (memberId: number) => {
  return request.get(`/settings/teams/members/disable/${memberId}`);
};

/**
 * 移除团队成员
 * DELETE /settings/team/{memberId}
 * 需要 Bearer Token 认证
 */
export const removeMember = (memberId: number) => {
  return request.delete(`/settings/teams/members/delete/${memberId}`);
};